import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getPolicies, acknowledgePolicy, askPolicyQuestion, GetPoliciesOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CheckCircle, Circle, FileText, Loader2, ExternalLink, MessageCircle, Send, ChevronRight, Download, StickyNote, Search, Shield, Info } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { Markdown } from '@/components/markdown';
import AddNoteDialog from '@/components/journal/AddNoteDialog';

type Policy = GetPoliciesOutputType['policies'][0];

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : ''; } catch { return ''; } };

const LOGO_URL = 'https://staffhub.wcdns.co.uk/rattlebone-logo.png';

function buildPolicyPdf(policy: Policy, contentHtml: string): string {
  const year = new Date().getFullYear();
  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>${policy.policyName}</title>
<style>*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#1a1a1a;line-height:1.6}.page{max-width:760px;margin:0 auto;padding:48px 40px}.header{display:flex;align-items:center;justify-content:space-between;padding-bottom:20px;border-bottom:3px solid #A36B58;margin-bottom:36px}.logo{height:52px}.header-label{font-size:13px;color:#888;font-weight:500;text-transform:uppercase}.content h1,.content h2,.content h3{margin:20px 0 10px;color:#1a1a1a}.content p{margin-bottom:14px;color:#333}.content ul,.content ol{margin:8px 0 16px 24px}.content li{margin-bottom:6px}.footer{margin-top:64px;padding-top:20px;border-top:1px solid #ddd;display:flex;align-items:center;justify-content:space-between}.footer-logo{height:28px;opacity:.5}.footer-text{font-size:11px;color:#aaa}@media print{@page{margin:18mm 20mm}}</style></head>
<body><div class="page"><div class="header"><img class="logo" src="${LOGO_URL}" alt="Rattlebone"/><span class="header-label">Company Policy — Rattlebone Sherston Ltd</span></div>
<h1 style="font-size:24px;font-weight:700;margin-bottom:32px;padding-bottom:12px;border-bottom:1px solid #eee">${policy.policyName}</h1>
<div class="content">${contentHtml}</div>
<div class="footer"><img class="footer-logo" src="${LOGO_URL}" alt="Rattlebone"/><span class="footer-text">© Rattlebone Sherston Ltd ${year}. Confidential — for staff use only.</span></div>
</div></body></html>`;
}

function PolicyCard({ policy, isAcknowledged, ackDate, isSelected, onClick }: {
  policy: Policy; isAcknowledged: boolean; ackDate?: string; isSelected: boolean; onClick: () => void;
}) {
  const isDraft = policy.lifecycleStatus === 'Draft' || policy.status === 'Draft';
  return (
    <button onClick={onClick}
      className={`w-full text-left border rounded-lg bg-card p-3.5 flex items-start gap-3 transition-colors ${
        isSelected ? 'ring-2 ring-primary border-primary/30' :
        isDraft ? 'border-yellow-500/30 hover:border-yellow-500/50' :
        !isAcknowledged ? 'border-primary/30 hover:bg-primary/5' :
        'border-border hover:bg-muted/50'
      }`}>
      <div className="mt-0.5 shrink-0">
        {isDraft ? <FileText className="w-4 h-4 text-yellow-600" /> :
         isAcknowledged ? <CheckCircle className="w-4 h-4 text-primary" /> :
         <Circle className="w-4 h-4 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{policy.policyName}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {policy.policyNumber && <span className="text-[10px] font-mono text-muted-foreground">{policy.policyNumber}</span>}
          {isDraft && <Badge variant="outline" className="text-[10px] h-4 px-1.5 text-yellow-700 bg-yellow-500/10 border-yellow-300">Draft</Badge>}
          {!isDraft && !isAcknowledged && <span className="text-xs text-primary">Requires acknowledgement</span>}
          {isAcknowledged && ackDate && <span className="text-xs text-muted-foreground">{fmtDate(ackDate)}</span>}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
    </button>
  );
}

function ProvenanceBanner({ policy }: { policy: Policy }) {
  if (!policy.sourceProvenance && !policy.changeSummary) return null;
  return (
    <div className="bg-muted/50 border border-border rounded-lg p-3 text-xs space-y-1">
      <p className="font-semibold text-foreground flex items-center gap-1"><Info className="w-3 h-3" /> Provenance & Import Info</p>
      {policy.policyNumber && <p><strong>Policy Code:</strong> {policy.policyNumber}</p>}
      {policy.version && <p><strong>Version:</strong> {policy.version}</p>}
      {policy.sourceProvenance && <p><strong>Source:</strong> {policy.sourceProvenance}</p>}
      {policy.reviewDate && <p><strong>Review Date:</strong> {fmtDate(policy.reviewDate)}</p>}
      {policy.changeSummary && <p><strong>Notes:</strong> {policy.changeSummary}</p>}
    </div>
  );
}

export default function Policies() {
  const { user } = useAuth();
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [acknowledgedIds, setAcknowledgedIds] = useState<string[]>([]);
  const [acknowledgements, setAcknowledgements] = useState<GetPoliciesOutputType['acknowledgements']>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewPolicy, setViewPolicy] = useState<Policy | null>(null);
  const [acknowledging, setAcknowledging] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  // Q&A
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [qa, setQa] = useState<{ q: string; a: string }[]>([]);
  const [streamedAnswer, setStreamedAnswer] = useState('');
  const qaRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [noteOpen, setNoteOpen] = useState(false);

  const load = useCallback(() => {
    if (!user) return;
    setLoading(true);
    getPolicies({
      includeDrafts: ['Admin', 'Owner'].includes(user.role || ''),
      search: search || undefined,
      category: category !== 'all' ? category : undefined,
    })
      .then(d => {
        setPolicies(d.policies);
        setAcknowledgedIds(d.acknowledgedPolicyIds);
        setAcknowledgements(d.acknowledgements);
        setIsAdmin(d.isAdmin);
        setCategories(d.categories);
      })
      .catch(() => toast.error('Failed to load policies'))
      .finally(() => setLoading(false));
  }, [user, search, category]);

  useEffect(() => { load(); }, [load]);

  const handleAcknowledge = async (policyId: string) => {
    setAcknowledging(true);
    try {
      await acknowledgePolicy({ policyId });
      setAcknowledgedIds(prev => [...prev, policyId]);
      toast.success('Policy acknowledged');
    } catch { toast.error('Failed to acknowledge'); }
    finally { setAcknowledging(false); }
  };

  const handleAskQuestion = async (q: string) => {
    if (!q.trim()) return;
    const questionText = q.trim();
    setQuestion('');
    setAsking(true);
    setStreamedAnswer('');
    let answer = '';
    try {
      const stream = askPolicyQuestion({ question: questionText, policyId: viewPolicy?.id });
      for await (const chunk of stream) { answer += chunk; setStreamedAnswer(answer); }
      setQa(prev => [...prev, { q: questionText, a: answer }]);
      setStreamedAnswer('');
    } catch { toast.error('Failed to get answer'); }
    finally { setAsking(false); }
    setTimeout(() => qaRef.current?.scrollTo({ top: qaRef.current.scrollHeight, behavior: 'smooth' }), 50);
  };

  const handleDownloadPdf = (policy: Policy) => {
    if (!contentRef.current) { toast.error('Open a policy first'); return; }
    const win = window.open('', '_blank', 'width=900,height=750');
    if (!win) { toast.error('Please allow pop-ups'); return; }
    win.document.write(buildPolicyPdf(policy, contentRef.current.innerHTML));
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 600);
  };

  if (loading) return (
    <div className="px-6 py-8 max-w-3xl mx-auto space-y-3">
      <Skeleton className="h-8 w-48" />{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16" />)}
    </div>
  );

  const drafts = policies.filter(p => p.lifecycleStatus === 'Draft' || p.status === 'Draft');
  const published = policies.filter(p => p.status === 'Published' && p.lifecycleStatus !== 'Draft');
  const pending = published.filter(p => !acknowledgedIds.includes(p.id));
  const acknowledged = published.filter(p => acknowledgedIds.includes(p.id));

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border px-6 py-5">
        <h1 className="text-xl font-bold text-foreground">Company Policies</h1>
        <p className="text-sm text-muted-foreground mt-1">Rattlebone Sherston Ltd — Read, acknowledge and ask questions about our policies</p>
      </div>

      <div className="px-6 py-6 max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-5 gap-6">
        {/* Policies list */}
        <div className="md:col-span-2 space-y-3">
          {/* Search & filters */}
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input placeholder="Search policies..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-9 text-sm" />
            </div>
            {categories.length > 1 && (
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Admin draft section */}
          {isAdmin && drafts.length > 0 && (
            <>
              <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wider flex items-center gap-1">
                <Shield className="w-3 h-3" /> Draft — Needs Review ({drafts.length})
              </p>
              {drafts.map(p => (
                <PolicyCard key={p.id} policy={p} isAcknowledged={false} isSelected={viewPolicy?.id === p.id} onClick={() => { setViewPolicy(p); setQa([]); setStreamedAnswer(''); }} />
              ))}
            </>
          )}

          {pending.length > 0 && (
            <>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Requires Acknowledgement ({pending.length})</p>
              {pending.map(p => (
                <PolicyCard key={p.id} policy={p} isAcknowledged={false} isSelected={viewPolicy?.id === p.id} onClick={() => { setViewPolicy(p); setQa([]); setStreamedAnswer(''); }} />
              ))}
            </>
          )}

          {acknowledged.length > 0 && (
            <>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-2">Acknowledged ({acknowledged.length})</p>
              {acknowledged.map(p => {
                const ack = acknowledgements.find(a => a.policyId === p.id);
                return (
                  <PolicyCard key={p.id} policy={p} isAcknowledged ackDate={ack?.acknowledgedAt} isSelected={viewPolicy?.id === p.id} onClick={() => { setViewPolicy(p); setQa([]); setStreamedAnswer(''); }} />
                );
              })}
            </>
          )}

          {policies.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No policies found</p>
            </div>
          )}
        </div>

        {/* Right panel: policy viewer */}
        <div className="md:col-span-3 space-y-4">
          {viewPolicy ? (
            <>
              <div className="border border-border rounded-xl bg-card p-5">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">{viewPolicy.policyName}</h2>
                    {viewPolicy.policyNumber && <p className="text-xs font-mono text-muted-foreground mt-0.5">{viewPolicy.policyNumber} · v{viewPolicy.version}</p>}
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setNoteOpen(true)}>
                      <StickyNote className="w-3.5 h-3.5" /> Note
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => handleDownloadPdf(viewPolicy)}>
                      <Download className="w-3.5 h-3.5" /> PDF
                    </Button>
                  </div>
                </div>

                {/* Provenance banner for admin */}
                {isAdmin && <ProvenanceBanner policy={viewPolicy} />}

                <div className="prose prose-sm max-w-none text-foreground mt-4" ref={contentRef}>
                  <Markdown>{viewPolicy.body}</Markdown>
                </div>

                {viewPolicy.attachmentUrl && (
                  <a href={viewPolicy.attachmentUrl} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline mt-4">
                    <ExternalLink className="w-3.5 h-3.5" /> View original document
                  </a>
                )}

                {/* Only show acknowledge for published policies */}
                {viewPolicy.status === 'Published' && viewPolicy.lifecycleStatus !== 'Draft' && (
                  !acknowledgedIds.includes(viewPolicy.id) ? (
                    <div className="mt-4 border-t border-border pt-4">
                      <Button onClick={() => handleAcknowledge(viewPolicy.id)} disabled={acknowledging} className="w-full">
                        {acknowledging ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'I Have Read and Understood This Policy'}
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-4 flex items-center gap-2 text-sm text-primary border-t border-border pt-3">
                      <CheckCircle className="w-4 h-4" /> Acknowledged
                    </div>
                  )
                )}

                {/* Draft notice for admin */}
                {(viewPolicy.lifecycleStatus === 'Draft' || viewPolicy.status === 'Draft') && isAdmin && (
                  <div className="mt-4 border-t border-border pt-4">
                    <div className="bg-yellow-500/10 border border-yellow-300 rounded-lg p-3 text-xs text-yellow-800">
                      <strong>Draft Policy</strong> — This policy is only visible to Admin/Owner. It will not appear for staff until published. Acknowledgement is OFF.
                    </div>
                  </div>
                )}
              </div>

              <AddNoteDialog
                open={noteOpen}
                onOpenChange={setNoteOpen}
                sourcePage={`Policy: ${viewPolicy.policyName}`}
                sourcePath="/policies"
              />
            </>
          ) : (
            <div className="border border-border rounded-xl bg-card p-5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Select a policy to read it</p>
              <p className="text-sm text-muted-foreground">Choose a policy from the list to view its full content and acknowledge it.</p>
            </div>
          )}

          {/* Q&A */}
          <div className="border border-border rounded-xl bg-card p-5">
            <div className="flex items-center gap-2 mb-4">
              <MessageCircle className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Ask About Policies</h3>
            </div>

            <div ref={qaRef} className="space-y-3 max-h-72 overflow-y-auto mb-3">
              {qa.map((item, i) => (
                <div key={i}>
                  <div className="bg-muted/50 rounded-lg px-3 py-2 text-sm mb-1">
                    <p className="text-xs text-muted-foreground font-medium mb-1">Question:</p>
                    <p className="text-foreground">{item.q}</p>
                  </div>
                  <div className="bg-primary/5 border border-primary/15 rounded-lg px-3 py-2 text-sm">
                    <p className="text-xs text-primary font-medium mb-1">Answer:</p>
                    <Markdown>{item.a}</Markdown>
                  </div>
                </div>
              ))}
              {(asking || streamedAnswer) && (
                <div className="bg-primary/5 border border-primary/15 rounded-lg px-3 py-2 text-sm">
                  <p className="text-xs text-primary font-medium mb-1">Answer:</p>
                  {streamedAnswer ? <Markdown>{streamedAnswer}</Markdown> : <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Thinking…</div>}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Input value={question} onChange={e => setQuestion(e.target.value)} placeholder="Ask your own question about our policies…"
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAskQuestion(question)} disabled={asking} className="flex-1 text-sm" />
              <Button onClick={() => handleAskQuestion(question)} disabled={asking || !question.trim()} className="shrink-0">
                {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Answers are AI-generated from published policies. For complex matters, always speak to your line manager.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
